import { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useAuth } from "./useAuth";
import { usePeptidePricing } from "./usePeptidePricing";
import { useTranslation } from "react-i18next";
import { applyFormSurcharge, hasAllFormsSelected } from "../components/peptides/peptideHelpers";
import { classifyCartSessionTransition } from "../utils/peptideCartSession";

const peptideCartContext = createContext();

const STORAGE_KEY_PREFIX = "peptideCart_";
const SAVED_CART_PREFIX = "peptideCartSaved_";
const PROVIDER_SNAPSHOTS_PREFIX = "peptideProviderSnaps_";
const SAVED_CART_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
// Coalesce rapid input edits (typing through several variants in a row) into
// one POST per provider. Long enough to avoid mid-edit saves; short enough
// that a quick blur+navigate-away still flushes before the next page load.
const BACKEND_SAVE_DEBOUNCE_MS = 500;

function getSavedCartKey(orgName, userId, brainId) {
  return SAVED_CART_PREFIX + (brainId || "noBrain") + "_" + (orgName || "default") + "_" + (userId || "unknown");
}

function stripCartForSave(items) {
  return items.map((item) => ({
    product: { id: item.product?.id, name: item.product?.name, image: item.product?.image, variants: [] },
    variant: { id: item.variant?.id, dosage: item.variant?.dosage },
    quantity: item.quantity,
    formType: item.formType,
    // Protocol quick-order metadata survives reloads so the checkout stamp and
    // prescription Rx fields don't silently vanish from a restored cart.
    ...(item.protocolMeta
      ? {
          protocolMeta: item.protocolMeta,
          indication: item.indication,
          dose: item.dose,
          route: item.route,
          frequency: item.frequency,
          duration: item.duration,
        }
      : {}),
  }));
}

function getStorageKey(orgName, brainId) {
  return STORAGE_KEY_PREFIX + (brainId || "noBrain") + "_" + (orgName || "default");
}

function getProviderSnapshotsKey(orgName, brainId) {
  return PROVIDER_SNAPSHOTS_PREFIX + (brainId || "noBrain") + "_" + (orgName || "default");
}

function usePeptideCartProvider() {
  const { t } = useTranslation();
  const { auth, brainId } = useAuth();
  const {
    resolvePrice,
    getPriceAtTier,
    activeSurchargeEnabled,
    isActiveSurchargeEnabledFor,
    isManufacturer,
    providerCustomPricing,
    providerRecurringDefaults,
    saveProviderCustomPricing: saveProviderCustomPricingPersisted,
    pricingLoaded,
    selectedProviderId,
  } = usePeptidePricing();

  // Fix 2 (whole-branch review): `activeSurchargeEnabled` on the hook only
  // reflects the CURRENT SESSION's own opt-out (provider/patient self-order —
  // see usePeptidePricing.js's `if (isProvider || isPatient)` gate) and stays
  // at its useState(true) default for a manufacturer session. When a
  // manufacturer is building a draft order FOR a selected provider, the
  // provider's own opt-out must govern instead — otherwise an opted-out
  // provider's draft still shows the phantom +$5. In-store manufacturer sales
  // (no selectedProviderId) have no provider to exempt, so they correctly
  // fall back to activeSurchargeEnabled (true).
  const effectiveSurchargeEnabled =
    isManufacturer && selectedProviderId ? isActiveSurchargeEnabledFor(selectedProviderId) : activeSurchargeEnabled;
  const orgName = auth?.session?.currentOrganizationName;
  // The auth payload carries the user id at auth.user.id (see Sentry.setUser in
  // useAuth and every other call site); session.id does not exist on at least
  // some brains, which left sessionIdentity permanently null — every post-auth
  // effect pass then classified as "logout", wiping the cart keys and disarming
  // persistence for the whole session.
  const userId = auth?.session?.id || auth?.user?.id;

  // useAuth initializes `auth` to undefined and only resolves it to a value
  // (logged in) or null (logged out) once checkAuth finishes. undefined ===
  // "still hydrating after a page load"; null === "confirmed no session." The
  // cart must NOT treat the hydration window as a logout — doing so wiped every
  // sessionStorage cart key on the first effect pass of every refresh, before
  // auth had a chance to resolve.
  const authResolved = auth !== undefined;

  // Build a session identity string so we can detect environment switches.
  // When any of these change (logout, login to different brain/org), the cart resets.
  // Use || as delimiter — safe because userId/brainId/orgName won't contain it.
  const sessionIdentity = userId && brainId && orgName ? `${userId}||${brainId}||${orgName}` : null;

  const [items, setItems] = useState([]);
  const orgLoadedRef = useRef(false);
  // Must start null, NOT sessionIdentity: when the provider first renders with
  // auth already resolved (fast session restore), seeding the ref with the
  // current identity made the first effect pass read as "noop" — so the
  // "switch" branch that loads the persisted cart and arms orgLoadedRef (the
  // sessionStorage write gate) never ran, and the cart silently became
  // memory-only for the whole session.
  const prevIdentityRef = useRef(null);
  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // Core effect: detect session changes (login, logout, environment switch)
  useEffect(() => {
    const prevIdentity = prevIdentityRef.current;
    const transition = classifyCartSessionTransition({ prevIdentity, sessionIdentity, authResolved });

    // Don't advance the identity ref during the hydration window — the real
    // resolve must still read as the initial transition once auth lands.
    if (transition === "hydrating") return;
    prevIdentityRef.current = sessionIdentity;

    if (transition === "logout") {
      // Logged out — save current cart to localStorage for later recovery, then clear
      if (prevIdentity && itemsRef.current.length > 0) {
        const [prevUser, prevBrain, prevOrg] = prevIdentity.split("||");
        try {
          localStorage.setItem(
            getSavedCartKey(prevOrg, prevUser, prevBrain),
            JSON.stringify({
              items: stripCartForSave(itemsRef.current),
              savedAt: Date.now(),
              orgName: prevOrg,
            })
          );
        } catch {}
      }
      // Wipe all peptide cart keys from sessionStorage
      try {
        const keysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && (key.startsWith(STORAGE_KEY_PREFIX) || key.startsWith(PROVIDER_SNAPSHOTS_PREFIX))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach((k) => sessionStorage.removeItem(k));
      } catch {}
      setItems([]);
      setProviderSnapshots({});
      orgLoadedRef.current = false;
      return;
    }

    if (transition === "switch") {
      // Session changed (new login or switched environment) — load from sessionStorage for this session
      let loadedItems = [];
      try {
        const stored = sessionStorage.getItem(getStorageKey(orgName, brainId));
        loadedItems = stored ? JSON.parse(stored) : [];
        setItems(loadedItems);
      } catch {
        setItems([]);
      }
      try {
        const snaps = sessionStorage.getItem(getProviderSnapshotsKey(orgName, brainId));
        setProviderSnapshots(snaps ? JSON.parse(snaps) : {});
      } catch {
        setProviderSnapshots({});
      }
      orgLoadedRef.current = true;

      // Migration cue: pre-`formType` carts in sessionStorage now block checkout
      // because items.every(formType === active|lyophilized) === false. The
      // per-item placeholder ("Select a form type") + the warning above the
      // Checkout button + the Set-All bar all already exist, but a returning
      // user may not connect "disabled checkout" to "stale cart needs form type
      // picked." Surface a one-shot toast so the migration is visible up front.
      const needsFormType = Array.isArray(loadedItems) && loadedItems.some((i) => i && !i.formType);
      if (needsFormType) {
        toast.info(
          t("hooks.usePeptideCart.selectFormTypeMigration", "Select Active or Lyophilized for each product before checkout."),
          { autoClose: 6000 }
        );
      }
    }
  }, [sessionIdentity, authResolved, orgName, brainId, t]);

  // Sync items to sessionStorage whenever they change
  useEffect(() => {
    if (!sessionIdentity || !orgLoadedRef.current) return;
    try {
      sessionStorage.setItem(getStorageKey(orgName, brainId), JSON.stringify(items));
    } catch {}
  }, [items, sessionIdentity, orgName, brainId]);

  const [savedCart, setSavedCart] = useState(null);
  const autoDismissTimerRef = useRef(null);

  useEffect(() => {
    if (!userId || !orgName || !brainId) return;
    try {
      const saveKey = getSavedCartKey(orgName, userId, brainId);
      const raw = localStorage.getItem(saveKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Date.now() - parsed.savedAt > SAVED_CART_TTL_MS) {
        localStorage.removeItem(saveKey);
        return;
      }
      setSavedCart(parsed);
      toast.info(t("hooks.usePeptideCart.itemsFromLastSession", { count: parsed.items.length }), {
        autoClose: 5000,
      });
      // Auto-dismiss saved cart after 30 seconds if user hasn't interacted
      autoDismissTimerRef.current = setTimeout(() => {
        try {
          localStorage.removeItem(saveKey);
        } catch {}
        setSavedCart(null);
        autoDismissTimerRef.current = null;
      }, 30000);
    } catch {}
    return () => {
      if (autoDismissTimerRef.current) {
        clearTimeout(autoDismissTimerRef.current);
        autoDismissTimerRef.current = null;
      }
    };
  }, [userId, orgName, brainId, t]);

  // `extras` lets flows like the protocol quick-order attach metadata
  // (protocolMeta, Rx fields, a formType default) that rides through
  // persistence and into the checkout payload untouched.
  const addItem = useCallback((product, variant, quantity = 1, extras = {}) => {
    setItems((prev) => {
      const existing = prev.find((item) => item.variant.id === variant.id);
      if (existing) {
        return prev.map((item) =>
          item.variant.id === variant.id ? { ...item, ...extras, quantity: item.quantity + quantity } : item
        );
      }
      return [...prev, { product, variant, quantity, formType: null, ...extras }];
    });
  }, []);

  const removeItem = useCallback((variantId) => {
    setItems((prev) => prev.filter((item) => item.variant.id !== variantId));
  }, []);

  // Transactional whole-cart update for flows that must detach + re-attach in a
  // single pass (checkout protocol-attach). Composing addItem/removeItem across
  // one click races React batching and the attach reducer's existing-line check.
  const replaceCartItems = useCallback((updater) => {
    setItems((prev) => (typeof updater === "function" ? updater(prev) : updater));
  }, []);

  // Undo companion to removeItem: re-inserts the removed line at its original
  // position with quantity/formType/extras intact. If the same variant was
  // re-added during the undo window, merge quantities instead of duplicating.
  const restoreItem = useCallback((item, index) => {
    if (!item?.variant?.id) return;
    setItems((prev) => {
      const existing = prev.find((i) => i.variant.id === item.variant.id);
      if (existing) {
        return prev.map((i) => (i.variant.id === item.variant.id ? { ...i, quantity: i.quantity + item.quantity } : i));
      }
      const next = [...prev];
      next.splice(Math.min(Math.max(index, 0), next.length), 0, item);
      return next;
    });
  }, []);

  const updateQuantity = useCallback((variantId, quantity) => {
    if (quantity < 1) return;
    setItems((prev) => prev.map((item) => (item.variant.id === variantId ? { ...item, quantity } : item)));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const setItemCustomPrice = useCallback((variantId, priceCents) => {
    setItems((prev) => prev.map((item) => (item.variant.id === variantId ? { ...item, customPriceCents: priceCents } : item)));
  }, []);

  // Reverts every in-cart line to the provider's tier price. Sets null (an
  // EXPLICIT revert) rather than undefined so saveProviderSnapshot's merge
  // deletes exactly these variants' overrides while leaving the provider's
  // other persisted overrides (variants not in the cart) untouched. undefined
  // would read as "untouched" and preserve the override — the opposite intent.
  const clearAllCustomPrices = useCallback(() => {
    setItems((prev) => prev.map((item) => (item.customPriceCents != null ? { ...item, customPriceCents: null } : item)));
  }, []);

  const setItemFormType = useCallback((variantId, formType) => {
    if (formType !== "active" && formType !== "lyophilized") return;
    setItems((prev) => prev.map((item) => (item.variant.id === variantId ? { ...item, formType } : item)));
  }, []);

  const setItemRequestedFulfillment = useCallback((variantId, value) => {
    if (value !== "back_order" && value !== null) return;
    setItems((prev) => prev.map((item) => (item.variant.id === variantId ? { ...item, requestedFulfillment: value } : item)));
  }, []);

  const bulkSetFormType = useCallback((productId, formType) => {
    if (formType !== "active" && formType !== "lyophilized") return;
    setItems((prev) => prev.map((item) => (item.product.id === productId ? { ...item, formType } : item)));
  }, []);

  const setAllFormTypes = useCallback((formType) => {
    if (formType !== "active" && formType !== "lyophilized") return;
    setItems((prev) => prev.map((item) => ({ ...item, formType })));
  }, []);

  const [recurringConfig, setRecurringConfig] = useState({ enabled: false, intervalDays: 30 });

  // ── Per-provider overrides: snapshot & restore custom prices + recurring when switching providers ──
  const snapshotsStorageKey = getProviderSnapshotsKey(orgName, brainId);
  const [providerSnapshots, setProviderSnapshots] = useState(() => {
    try {
      const stored = sessionStorage.getItem(snapshotsStorageKey);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  // Keep a ref in sync so restoreProviderSnapshot always reads latest snapshots
  const snapshotsRef = useRef(providerSnapshots);
  useEffect(() => {
    snapshotsRef.current = providerSnapshots;
  }, [providerSnapshots]);

  // Persist snapshots to sessionStorage whenever they change. SessionStorage is
  // a per-tab draft cache so unsaved typing survives a refresh; the backend is
  // the source of truth across sessions.
  useEffect(() => {
    try {
      sessionStorage.setItem(snapshotsStorageKey, JSON.stringify(providerSnapshots));
    } catch {}
  }, [providerSnapshots, snapshotsStorageKey]);

  // Debounce backend save so rapid input edits coalesce into one POST.
  const backendSaveTimerRef = useRef({});

  const saveProviderSnapshot = useCallback(
    (providerId) => {
      if (!providerId) return;
      // Merge cart edits ONTO the provider's persisted overrides instead of
      // replacing the whole map with only the in-cart variants. The backend
      // save is full-replace per provider, so building the map from cart items
      // alone silently deleted every override for a variant that happened not to
      // be in the current cart. Per-line semantics:
      //   number          → set/replace that variant's override
      //   null            → explicit per-line revert (PeptideCartItem clear /
      //                      clearAllCustomPrices) → delete the override
      //   undefined        → line was never touched this session → leave the
      //                      persisted override untouched
      const persisted = (isManufacturer && providerCustomPricing?.[providerId]) || {};
      const customPrices = { ...persisted };
      items.forEach((item) => {
        const v = item.customPriceCents;
        if (v == null) {
          // null = explicit revert; undefined never reaches here as a clear
          // because an untouched line carries no key change — but a line the
          // user explicitly reverted DOES carry null, so honor it.
          if (v === null) delete customPrices[item.variant.id];
        } else {
          customPrices[item.variant.id] = v;
        }
      });
      const snapshot = { customPrices, recurringConfig: { ...recurringConfig } };
      snapshotsRef.current = { ...snapshotsRef.current, [providerId]: snapshot };
      setProviderSnapshots(snapshotsRef.current);

      // Persist to backend (debounced ~500ms) so prices survive logout / new session.
      // Manufacturer-only — providers don't set per-provider overrides.
      if (isManufacturer && saveProviderCustomPricingPersisted) {
        // Only round-trip the recurring default when it actually differs from
        // what's already persisted. Avoids writing recurring data on every
        // price-only edit (the user may not have touched the toggle at all).
        const persistedRecurring = providerRecurringDefaults?.[providerId];
        const sameRecurring =
          persistedRecurring &&
          persistedRecurring.enabled === recurringConfig.enabled &&
          persistedRecurring.intervalDays === recurringConfig.intervalDays;
        const recurringPayload = sameRecurring ? null : { ...recurringConfig };

        if (backendSaveTimerRef.current[providerId]) {
          clearTimeout(backendSaveTimerRef.current[providerId]);
        }
        backendSaveTimerRef.current[providerId] = setTimeout(() => {
          saveProviderCustomPricingPersisted(providerId, customPrices, recurringPayload).catch(() => {
            // Pricing hook surfaces a toast and rolls back optimistic state.
            // SessionStorage snapshot is preserved as a draft cache.
          });
          delete backendSaveTimerRef.current[providerId];
        }, BACKEND_SAVE_DEBOUNCE_MS);
      }
    },
    [items, recurringConfig, isManufacturer, saveProviderCustomPricingPersisted, providerRecurringDefaults, providerCustomPricing]
  );

  // Prefer backend-persisted custom pricing over sessionStorage when both
  // exist. SessionStorage remains the offline / pre-load fallback (e.g. on
  // first paint before refreshPricing() resolves, or when backend save fails).
  const restoreProviderSnapshot = useCallback(
    (providerId) => {
      if (!providerId) {
        setItems((prev) => prev.map((item) => ({ ...item, customPriceCents: undefined })));
        setRecurringConfig({ enabled: false, intervalDays: 30 });
        return;
      }
      const persistedPrices = (isManufacturer && providerCustomPricing?.[providerId]) || null;
      const persistedRecurring = (isManufacturer && providerRecurringDefaults?.[providerId]) || null;
      const sessionSnapshot = snapshotsRef.current[providerId] || null;
      const customPrices = persistedPrices || sessionSnapshot?.customPrices || {};
      const recurring = persistedRecurring || sessionSnapshot?.recurringConfig || { enabled: false, intervalDays: 30 };

      setItems((prev) =>
        prev.map((item) => {
          const saved = customPrices[item.variant.id];
          return { ...item, customPriceCents: saved != null ? saved : undefined };
        })
      );
      setRecurringConfig(recurring);
    },
    [isManufacturer, providerCustomPricing, providerRecurringDefaults]
  );

  // One-shot migration: if sessionStorage has snapshots from before the
  // backend-persistence change shipped, POST them once so existing users don't
  // lose in-flight pricing the first time they reload after the deploy.
  //
  // Gated on pricingLoaded — without it, the initial empty providerCustomPricing
  // ({} before refreshPricing resolves) looks identical to "no remote data" and
  // we'd POST stale local snapshots over fresh remote data that's about to load.
  const migratedRef = useRef(false);
  useEffect(() => {
    if (migratedRef.current) return;
    if (!isManufacturer || !saveProviderCustomPricingPersisted) return;
    if (!pricingLoaded) return;
    const local = snapshotsRef.current || {};
    const remote = providerCustomPricing || {};
    const candidates = Object.keys(local).filter((providerId) => {
      const localPrices = local[providerId]?.customPrices || {};
      const remotePrices = remote[providerId] || {};
      const hasLocal = Object.keys(localPrices).length > 0;
      const hasRemote = Object.keys(remotePrices).length > 0;
      return hasLocal && !hasRemote;
    });
    migratedRef.current = true;
    if (candidates.length === 0) return;
    candidates.forEach((providerId) => {
      const snapshot = local[providerId];
      saveProviderCustomPricingPersisted(providerId, snapshot.customPrices || {}, snapshot.recurringConfig).catch(() => {
        // Rolled back inside the hook + toast surfaced. Migration is best-effort.
      });
    });
  }, [isManufacturer, saveProviderCustomPricingPersisted, providerCustomPricing, pricingLoaded]);

  // Initial restore on mount: when the cart resumes a session with a provider
  // already selected (via persisted state in usePeptidePricing), apply that
  // provider's saved overrides to currently-loaded items as soon as the
  // backend pricing data arrives. Runs once per provider+pricingLoaded
  // transition; subsequent provider switches go through handleSelectProvider.
  const initialRestoredRef = useRef("");
  useEffect(() => {
    if (!pricingLoaded || !isManufacturer || !selectedProviderId) return;
    if (initialRestoredRef.current === selectedProviderId) return;
    initialRestoredRef.current = selectedProviderId;
    restoreProviderSnapshot(selectedProviderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingLoaded, isManufacturer, selectedProviderId]);

  // Clear pending backend-save timers on unmount so a navigation-away doesn't
  // leak a delayed POST tied to stale state.
  useEffect(() => {
    const timers = backendSaveTimerRef.current;
    return () => {
      Object.values(timers).forEach((id) => clearTimeout(id));
    };
  }, []);

  const itemCount = useMemo(() => items.reduce((sum, item) => sum + item.quantity, 0), [items]);
  const hasCustomPricing = useMemo(() => items.some((item) => item.customPriceCents != null), [items]);
  // Supplies (e.g. bacteriostatic water, syringes) are not peptides — they
  // don't have an active/lyophilized physical form, so we don't gate their
  // line on a form-type pick. Real peptides still must have a form selected.
  // Delegated to hasAllFormsSelected so the checkout's fail-closed guard
  // shares one predicate with this memo.
  const allFormsSelected = useMemo(() => hasAllFormsSelected(items), [items]);

  // Resolve price: in-cart override > effective resolved price > raw tier price
  // > variant default, then add the active-form surcharge if the line is
  // "active". `resolvePrice` is the SAME resolver the cart line displays
  // (PeptideCartItem), so the subtotal and the per-line price never diverge —
  // it layers in the provider's own custom pricing (`ownCustomPricing`) and the
  // manufacturer's per-provider overrides (`providerCustomPricing`), which a raw
  // `getPriceAtTier` lookup silently dropped. `getPriceAtTier(tier)` stays as a
  // fallback for the explicit-tier path (e.g. a comparison total at a tier other
  // than the active one) where `resolvePrice` — which is bound to the active
  // tier — has nothing to offer. The active-form surcharge still layers on top
  // of every pricing path so the +$5/unit physical-form cost is consistent and
  // can't be absorbed by a custom price.
  const getItemPrice = useCallback(
    (item, tier) => {
      const base =
        item.customPriceCents != null
          ? item.customPriceCents
          : (resolvePrice(item.variant.id) ?? getPriceAtTier(item.variant.id, tier) ?? item.variant.priceCents);
      return applyFormSurcharge(base, item.formType, effectiveSurchargeEnabled);
    },
    [resolvePrice, getPriceAtTier, effectiveSurchargeEnabled]
  );

  const getSubtotal = useCallback(
    (tier) => {
      return items.reduce((sum, item) => sum + getItemPrice(item, tier) * item.quantity, 0);
    },
    [items, getItemPrice]
  );

  // Legacy: subtotalCents uses Tier D as default for backwards compatibility
  const subtotalCents = useMemo(() => getSubtotal("D"), [getSubtotal]);

  const restoreCart = useCallback(() => {
    if (autoDismissTimerRef.current) {
      clearTimeout(autoDismissTimerRef.current);
      autoDismissTimerRef.current = null;
    }
    if (!savedCart?.items) return { restored: 0, skipped: 0 };
    // Saved items already contain full product/variant objects
    const valid = savedCart.items.filter((item) => item.product && item.variant);
    if (valid.length > 0) {
      setItems((prev) => {
        const existingIds = new Set(prev.map((item) => item.variant.id));
        const newItems = valid.filter((item) => !existingIds.has(item.variant.id));
        return [...prev, ...newItems];
      });
    }
    try {
      localStorage.removeItem(getSavedCartKey(orgName, userId, brainId));
    } catch {}
    setSavedCart(null);
    return { restored: valid.length, skipped: savedCart.items.length - valid.length };
  }, [savedCart, orgName, userId, brainId]);

  const dismissSavedCart = useCallback(() => {
    if (autoDismissTimerRef.current) {
      clearTimeout(autoDismissTimerRef.current);
      autoDismissTimerRef.current = null;
    }
    try {
      localStorage.removeItem(getSavedCartKey(orgName, userId, brainId));
    } catch {}
    setSavedCart(null);
  }, [orgName, userId, brainId]);

  return {
    items,
    addItem,
    removeItem,
    replaceCartItems,
    restoreItem,
    updateQuantity,
    clearCart,
    itemCount,
    subtotalCents,
    getItemPrice,
    // The buyer-context-aware surcharge flag getItemPrice already resolves —
    // exposed so PeptideCartItem's own price/label math reads one source of
    // truth instead of recomputing the manufacturer-selected-provider case.
    effectiveSurchargeEnabled,
    getSubtotal,
    setItemCustomPrice,
    clearAllCustomPrices,
    hasCustomPricing,
    setItemFormType,
    bulkSetFormType,
    setAllFormTypes,
    allFormsSelected,
    setItemRequestedFulfillment,
    recurringConfig,
    setRecurringConfig,
    savedCart,
    restoreCart,
    dismissSavedCart,
    saveProviderSnapshot,
    restoreProviderSnapshot,
  };
}

export function ProvidePeptideCart({ children }) {
  const values = usePeptideCartProvider();
  return <peptideCartContext.Provider value={values}>{children}</peptideCartContext.Provider>;
}

export const usePeptideCart = () => {
  const ctx = useContext(peptideCartContext);
  if (!ctx) {
    return {
      items: [],
      addItem: () => {},
      removeItem: () => {},
      replaceCartItems: () => {},
      restoreItem: () => {},
      updateQuantity: () => {},
      clearCart: () => {},
      itemCount: 0,
      subtotalCents: 0,
      getItemPrice: () => 0,
      effectiveSurchargeEnabled: true,
      getSubtotal: () => 0,
      setItemCustomPrice: () => {},
      hasCustomPricing: false,
      setItemFormType: () => {},
      bulkSetFormType: () => {},
      setAllFormTypes: () => {},
      allFormsSelected: false,
      setItemRequestedFulfillment: () => {},
      recurringConfig: { enabled: false, intervalDays: 30 },
      setRecurringConfig: () => {},
      savedCart: null,
      restoreCart: () => ({ restored: 0, skipped: 0 }),
      dismissSavedCart: () => {},
      saveProviderSnapshot: () => {},
      restoreProviderSnapshot: () => {},
    };
  }
  return ctx;
};
